"use client";

import { useState } from "react";
import PhoneInput from "@/components/PhoneInput";
import { useToast } from "@/components/ui/Toast";
import { useLocale } from "@/lib/locale-context";
import { track } from "@/lib/metrika";

// Проверка бонусного счёта — 1:1 со Солнышко (BonusCheckClient), акцент синий.
// Телефон + 2 согласия → POST /api/v1/bonus/check → ФИО + баланс баллов.
const ACCENT = "#0039A6";

type Result = { kind: "found"; fio: string; sum: number } | { kind: "notfound" } | null;

function Check({ checked, onToggle, children }: { checked: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
      <button
        type="button"
        aria-pressed={checked}
        onClick={onToggle}
        style={{
          marginTop: 1, width: 18, height: 18, flexShrink: 0, cursor: "pointer", borderRadius: 5,
          display: "flex", alignItems: "center", justifyContent: "center",
          border: checked ? `1px solid ${ACCENT}` : "1px solid #C9D3E6",
          background: checked ? ACCENT : "#fff", transition: "all 0.15s",
        }}
      >
        {checked && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>}
      </button>
      <span onClick={onToggle} style={{ cursor: "pointer" }}>{children}</span>
    </div>
  );
}

export default function BonusCheckClient() {
  const { t } = useLocale();
  const toast = useToast();
  const [phone, setPhone] = useState("");
  const [dial, setDial] = useState("+7");
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreePersonal, setAgreePersonal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Result>(null);

  const digits = phone.replace(/\D/g, "");
  const canCheck = digits.length >= 10 && agreePrivacy && agreePersonal && !loading;

  async function check() {
    if (!agreePrivacy || !agreePersonal) { toast.show(t("consent.confirm"), "error"); return; }
    if (digits.length < 10) { toast.show(t("bonus.enterPhone"), "error"); return; }
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch("/api/v1/bonus/check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: `${dial}${digits}` }),
      });
      const data = await res.json();
      track("bonus_check");
      if (data.found) setResult({ kind: "found", fio: data.fio, sum: data.sum });
      else setResult({ kind: "notfound" });
    } catch {
      toast.show(t("bonus.checkFail"), "error");
    } finally {
      setLoading(false);
    }
  }

  const fmtSum = (n: number) => Number.isInteger(n) ? n.toLocaleString("ru-RU") : n.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div style={{ maxWidth: 560 }}>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#8E8E93", marginBottom: 6 }}>{t("bonus.phoneLabel")}</label>
      <PhoneInput value={phone} onChange={(p, d) => { setPhone(p); setDial(d); }} height={52} />

      <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10, fontSize: 13, color: "#4A4A50", lineHeight: 1.45 }}>
        <Check checked={agreePrivacy} onToggle={() => setAgreePrivacy(!agreePrivacy)}>
          {t("consent.privacyPrefix")}{" "}
          <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: ACCENT, textDecoration: "underline" }}>{t("consent.privacySite")}</a>
        </Check>
        <Check checked={agreePersonal} onToggle={() => setAgreePersonal(!agreePersonal)}>
          {t("consent.personalPrefixReg")}{" "}
          <a href="/legal/personal-data" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: ACCENT, textDecoration: "underline" }}>{t("consent.personalData")}</a>
        </Check>
      </div>

      <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={check}
          disabled={!canCheck}
          style={{
            width: "100%", maxWidth: 260, height: 52, borderRadius: 10, border: "none",
            background: canCheck ? ACCENT : "#E0E0E5", color: "#fff", fontSize: 16, fontWeight: 700,
            cursor: canCheck ? "pointer" : "default", fontFamily: "inherit",
          }}
        >
          {loading ? "…" : t("bonus.check")}
        </button>
      </div>

      {result?.kind === "found" && (
        <div style={{ marginTop: 24, padding: "20px 22px", background: "#EAF0FB", border: "1px solid #C9DBF7", borderRadius: 14 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#1E1E2D", marginBottom: 8 }}>{result.fio}</div>
          <div style={{ fontSize: 15, color: "#4A4A50" }}>
            {t("bonus.balancePrefix")} <b style={{ color: ACCENT, fontSize: 20 }}>{fmtSum(result.sum)}</b> {t("bonus.points")}
          </div>
        </div>
      )}
      {result?.kind === "notfound" && (
        <div style={{ marginTop: 24, padding: "16px 18px", background: "#F7F7FA", borderRadius: 14, fontSize: 15, color: "#4A4A50" }}>
          {t("bonus.notFound")}
        </div>
      )}
    </div>
  );
}
